import React, {useRef} from 'react';

import {
  startsWith,
  endsWith,
  some,
  attempt,
  delay,
  noop,
  isEmpty,
  includes,
} from 'lodash-es';

import UserApi from 'static/three-oh/src/modules/apis/User/UserApi';
import {signUpRoot} from 'static/three-oh/src/modules/constants/routePaths';
import SignInForm from 'static/three-oh/src/routes/SignIn/components/SignInForm';

const allowedForwardToProtocols = [
  'http:',
  'https:',
];

const allowForwardToHostnames = [
  // Production.
  'newsela.com',
  // Staging.
  'newseladev.com',
  // Local development.
  'localhost',
];

const hostnameMatches = (target, source) => {
  // Match exact hostname or subdomains.
  return source === target || endsWith(source, `.${target}`);
};

export const isUrlSafe = (nextUrl) => {
  if (startsWith(nextUrl, '/')) {
    return true;
  } else {
    try {
      const {protocol, hostname} = new URL(nextUrl);
      if (!includes(allowedForwardToProtocols, protocol)) return false;
      return some(allowForwardToHostnames, (target) => hostnameMatches(target, hostname));
    } catch (e) {
      return false;
    }
  }
};

// eslint-disable-next-line no-empty-function
const useConstructor = (callback = () => {}) => {
  const hasBeenCalled = useRef(false);
  if (hasBeenCalled.current) return;
  callback();
  hasBeenCalled.current = true;
};

const SignInContainer = () => {
  let nextUrl = null;
  let joinUrl = null;

  useConstructor(() => {
    const queryStringParams = new URLSearchParams(window.location.search);
    nextUrl = queryStringParams.get('next');
    joinUrl = queryStringParams.get('join_url') || signUpRoot;

    // Defend against open redirect vulnerabilities by ignoring any next url
    // that is not a newsela.com subdomain or doesn't begin with a forward slash, eg:
    // ?next=javascript:alert(document.cookie)
    if (!isUrlSafe(nextUrl)) {
      nextUrl = null;
    }

    if (!isUrlSafe(joinUrl)) {
      joinUrl = signUpRoot;
    }

    // Also don't allow recursive nexts to a sign-in page. If we're signing
    // in, we don't want to be kicking back to sign-in again.
    if (nextUrl && nextUrl.indexOf('signin') > -1) {
      nextUrl = null;
    }

    // But if we are on a particular page, let's ensure the next state is that
    // page _if_ it's not signin.
    if (!nextUrl && window.location.href.indexOf('signin') == -1) {
      nextUrl = window.location.href;
    }
  });

  /**
   * TODO:
   * investigate relocating this into the base layout urchins, Marketo advises against calling
   * associateLead if the page will quickly navigate away as the request might not finish.
   */
  const identifyMarketoLead = () => {
    // Find out who is logged in and identify them to Marketo.
    // Associates the current _mkto_trk activity tracking cookie with a Marketo lead record.

    const trackLead = (user) => {
      const munchkinHash = user.marketo_munchkin_hash;
      if (!munchkinHash) {
        // No Marketo lead on record for this user, abort!
        return Promise.resolve();
      }

      const data = {
        Email: user.email,
        external_id: user.external_id,
        FirstName: user.first_name,
        LastName: user.last_name,
      };

      // Marketo doesn't provide an async callback for their identifier function, so instead we
      // chill for ~500ms to give it a chance to run before resolving.
      return new Promise((resolve) => {
        attempt(() => window.Munchkin.munchkinFunction('associateLead', data, munchkinHash));
        delay(resolve, 500);
      });
    };

    return UserApi.getCurrentUser().then(trackLead).catch(noop);
  };

  const redirectUser = () => {
    const shouldUseNextUrl = !isEmpty(nextUrl) && window.location.href.split('#')[0] !== nextUrl.split('#')[0];
    if (shouldUseNextUrl) {
      window.location.href = nextUrl;
    } else if (includes(window.location.href, 'signin')) {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  };

  return (
    <SignInForm
      identifyMarketoLead={identifyMarketoLead}
      joinUrl={joinUrl}
      nextUrl={nextUrl}
      redirectUser={redirectUser}
    />
  );
};

export default SignInContainer;
